import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../stores/authStore';

export default function LegalAccept() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuthStore();
  const [requiredVersion, setRequiredVersion] = useState(null);
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const alreadyAccepted = useMemo(() => {
    const acceptedAt = user?.legal?.termsAcceptedAt;
    const acceptedVersion = user?.legal?.termsVersion;
    return !!acceptedAt && (!requiredVersion || acceptedVersion === requiredVersion);
  }, [user?.legal?.termsAcceptedAt, user?.legal?.termsVersion, requiredVersion]);

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        const res = await api.get('/auth/legal/terms');
        if (!canceled) setRequiredVersion(res.data?.version || null);
      } catch (e) {
        if (!canceled) setRequiredVersion(null);
      }
    };
    load();
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (alreadyAccepted) {
      navigate('/', { replace: true });
    }
  }, [alreadyAccepted, navigate]);

  const canSubmit = checked && typed.trim().toUpperCase() === 'I AGREE' && !isSubmitting;

  const handleAccept = async () => {
    setError(null);
    if (!requiredVersion) {
      setError('Unable to load terms version. Please refresh.');
      return;
    }
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);
      const res = await api.post('/auth/legal/accept', { version: requiredVersion });
      updateUser({ legal: res.data?.legal || null });
      navigate('/', { replace: true });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to record acceptance');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 text-dark-100">
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-14 space-y-6">
        <header className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Link
              to="/legal"
              className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Read full legal terms
            </Link>
            <button className="text-sm text-dark-400 hover:text-white" onClick={logout} type="button">
              Logout
            </button>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">Accept Terms to Continue</h1>
          <p className="text-sm text-dark-300">
            You must accept the Terms of Use & Risk Disclosure to use BetMimic.
          </p>
        </header>

        <section className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 mt-0.5" />
            <div className="space-y-2">
              <h2 className="text-base font-semibold text-red-100">High-Risk Trading Warning</h2>
              <ul className="list-disc list-inside text-sm text-red-100/90 space-y-1">
                <li>You can lose all funds you deploy.</li>
                <li>Automation can amplify losses faster than manual trading.</li>
                <li>We are not responsible for trading outcomes, missed signals, or execution drift.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-dark-700 bg-dark-800/70 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold">Clickwrap acceptance</h2>
          </div>

          <p className="text-sm text-dark-300">
            Required terms version: <span className="text-dark-100 font-semibold">{requiredVersion || '…'}</span>
          </p>

          {error ? (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
              {error}
            </div>
          ) : null}

          <label className="flex items-start gap-3 text-sm text-dark-200 select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1"
            />
            <span>
              I have read and agree to the <Link to="/legal" className="text-primary-400 hover:text-primary-300">Terms of Use & Risk Disclosure</Link>.
            </span>
          </label>

          <div className="space-y-2">
            <p className="text-xs text-dark-500">
              Type <span className="text-dark-200 font-semibold">I AGREE</span> to confirm.
            </p>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="input"
              placeholder="I AGREE"
            />
          </div>

          <button
            onClick={handleAccept}
            disabled={!canSubmit}
            className="btn btn-gradient w-full"
            type="button"
          >
            {isSubmitting ? 'Submitting…' : 'Accept and Continue'}
          </button>

          <p className="text-xs text-dark-500">
            If you do not agree, you must stop using the Service.
          </p>
        </section>
      </div>
    </div>
  );
}
